import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../lib/api.js';
import { deltaColor, fmtMoney } from '../lib/format.js';
import { fmtUsd, fmtUsd2 } from '../lib/format.js';

// as_of is when Square was last pulled (Tally polls every 15 min) — shown in ET
// so the number never claims a freshness it doesn't have.
function etClock(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString('en-US',
      { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
  } catch { return null; }
}

// How long someone's been on the clock, compact (e.g. "2h 10m", "45m").
function sinceLabel(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!(ms > 0)) return null;
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// Normalize a store's cogs-today payload into card fields + the day's
// estimated net margin (net sales − accumulated labor − theoretical cogs).
// cogs is null when nothing today can be costed yet → margin stays null, never
// a fabricated zero.
function cogsFields(cg, revenue, laborCost) {
  const available = !!(cg && cg.available !== false && cg.cogs != null);
  const cogs = available ? cg.cogs : null;
  return {
    cogsAvailable: available,
    cogs,
    foodCostPct: available ? cg.food_cost_pct : null,
    costedPct: cg ? cg.costed_pct : null,
    costedRevenue: available ? cg.costed_revenue : 0,
    cogsAsOf: cg?.as_of || null,
    cogsStale: !!cg?.stale,
    netMargin: cogs != null ? revenue - (laborCost || 0) - cogs : null,
  };
}

// ─── Owner Dashboard (cross-location aggregate) ──────────────────────────────
// Owner-only top-level view that fans out to /api/realtime/{key} for all four
// café keys and rolls them up: total revenue today + WoW delta, total
// transactions today + WoW, per-location card grid, plus the day's top
// reliability concerns surfaced from /api/dashboard.
export function OwnerDashboard({ user, locations }) {
  const [byLoc, setByLoc] = useState({});
  const [cogsByLoc, setCogsByLoc] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);

  // Phase D6: derive location keys from the locations prop (loaded
  // server-side from the DB) instead of the prior hardcoded 4-list.
  // The realtime route's path segment matches the lowercase-snake
  // form of the location name — same transform shared/realtime-
  // dashboard.js's setLocations() uses at boot.
  const locationKeys = useMemo(() => {
    if (!Array.isArray(locations) || locations.length === 0) return [];
    return locations
      .map(l => String(l?.name || '').trim())
      .filter(Boolean)
      .map(name => name.toLowerCase().replace(/\s+/g, '_'));
  }, [locations]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (locationKeys.length === 0) { setLoading(false); return; }
    if (!silent) setLoading(true);
    try {
      const rows = await Promise.all(locationKeys.map(k =>
        api('GET', `/api/realtime/${k}`).then(d => [k, d]).catch(e => [k, { error: e.message || String(e) }])
      ));
      setByLoc(Object.fromEntries(rows));
      setRefreshedAt(new Date());

      // Theoretical COGS per store from Tally (server-side proxy). Fetch by the
      // CANONICAL store name — the realtime keys are snake_case and don't
      // round-trip "JT Connell" — and key the result by that same snake key so
      // it lines up with the cards. Keep the last good value on a blip; a
      // non-200 must never blank the tile or show a zero.
      const cogsRows = await Promise.all((locations || []).map((l) => {
        const key = String(l?.name || '').trim().toLowerCase().replace(/\s+/g, '_');
        return api('GET', `/api/operations/cogs-today?location=${encodeURIComponent(l.name)}`)
          .then((d) => [key, d]).catch(() => [key, null]);
      }));
      setCogsByLoc((prev) => {
        const next = { ...prev };
        for (const [k, d] of cogsRows) {
          if (d && d.available !== false) next[k] = d;              // fresh good value
          else if (next[k]) next[k] = { ...next[k], stale: true };  // keep last good
          else next[k] = d || { available: false };                // nothing yet
        }
        return next;
      });
    } finally { setLoading(false); }
  }, [locationKeys, locations]);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh every 60s — matches RealtimeDashboard cadence.
  useEffect(() => {
    const t = setInterval(() => load({ silent: true }), 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const cards = Object.entries(byLoc).map(([key, d]) => {
    if (d?.error) return { key, error: d.error };
    const sc = d?.scorecard || {};
    return {
      key,
      name: d?.meta?.locationName || key,
      revenue: sc.revenue?.value || 0,
      revenueDelta: sc.revenue?.deltaPct,
      revenueYoY: sc.revenue?.yoyDeltaPct ?? null,
      revenueFlag: sc.revenue?.flag || 'gray',
      tx: sc.transactions?.value || 0,
      txDelta: sc.transactions?.deltaPct,
      txYoY: sc.transactions?.yoyDeltaPct ?? null,
      avgTicket: sc.avgTicket?.value || 0,
      avgTicketDelta: sc.avgTicket?.deltaPct,
      avgTicketYoY: sc.avgTicket?.yoyDeltaPct ?? null,
      onClock: d?.schedule?.staffOnClock ?? null,
      scheduled: d?.schedule?.staffScheduled ?? null,
      // Live labor — per CJ: "labor rate calculated by sales / (sum of hours
      // × pay rate for all employees clocked in)". The server already
      // computes labor cost (sum of hours × hourly_rate) and the labor% (cost/
      // revenue × 100). We surface both.
      laborPct: sc.laborPct?.value ?? null,
      laborCost: sc.laborPct?.laborCostToday ?? null,
      laborHours: sc.laborPct?.laborHoursToday ?? null,
      laborFlag: sc.laborPct?.flag || 'gray',
      laborNote: sc.laborPct?.note || null,
      // Current-hour live metrics:
      hourPct: sc.laborPct?.currentHourPct ?? null,
      hourFlag: sc.laborPct?.currentHourFlag || 'gray',
      hourCost: sc.laborPct?.currentHourCost ?? null,
      hourSales: sc.laborPct?.currentHourSales ?? null,
      burnRate: sc.laborPct?.burnRatePerHour ?? null,
      onClock: sc.laborPct?.onClockCount ?? 0,
      onClockPeople: sc.laborPct?.onClockPeople || [],
      ...cogsFields(cogsByLoc[key], sc.revenue?.value || 0, sc.laborPct?.laborCostToday ?? null),
    };
  });

  const totals = cards.filter(c => !c.error).reduce(
    (a, c) => ({
      revenue: a.revenue + c.revenue,
      tx: a.tx + c.tx,
      laborCost: a.laborCost + (c.laborCost || 0),
      laborHours: a.laborHours + (c.laborHours || 0),
      hourCost: a.hourCost + (c.hourCost || 0),
      hourSales: a.hourSales + (c.hourSales || 0),
      burnRate: a.burnRate + (c.burnRate || 0),
      cogs: a.cogs + (c.cogs || 0),
      costedRev: a.costedRev + (c.costedRevenue || 0),
      anyCogs: a.anyCogs || c.cogsAvailable,
    }),
    { revenue: 0, tx: 0, laborCost: 0, laborHours: 0, hourCost: 0, hourSales: 0, burnRate: 0, cogs: 0, costedRev: 0, anyCogs: false }
  );
  // Company-wide cumulative labor % for the day.
  totals.laborPct = totals.revenue > 0 && totals.laborCost > 0
    ? (totals.laborCost / totals.revenue) * 100
    : null;
  // Company-wide current-hour labor %.
  totals.hourPct = totals.hourSales > 0 && totals.hourCost > 0
    ? (totals.hourCost / totals.hourSales) * 100
    : null;
  // Two food-cost bases so the % ties to the $ (CJ 2026-07-18): foodCostPct is
  // cogs over COSTED sales (true food cost of what we can cost); blendedCogsPct
  // is the SAME real cogs over ALL sales (matches cogs$ ÷ revenue). They
  // converge as recipe coverage climbs to 100%. + the day's est. net margin.
  totals.foodCostPct = totals.costedRev > 0 ? (totals.cogs / totals.costedRev) * 100 : null;
  totals.blendedCogsPct = totals.anyCogs && totals.revenue > 0 ? (totals.cogs / totals.revenue) * 100 : null;
  totals.netMargin = totals.anyCogs ? totals.revenue - totals.laborCost - totals.cogs : null;
  totals.netMarginPct = (totals.netMargin != null && totals.revenue > 0)
    ? (totals.netMargin / totals.revenue) * 100 : null;
  // Stores share one Tally pull, so any card's as_of stamps the COGS block.
  const cogsAsOf = etClock(cards.find(c => c.cogsAsOf)?.cogsAsOf);
  const cogsStale = cards.some(c => c.cogsAvailable && c.cogsStale);

  function fmtMoney(n) { return '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
  function fmtPct(p) { if (p == null || isNaN(p)) return ''; const sign = p > 0 ? '+' : ''; return ` ${sign}${p.toFixed(1)}%`; }
  // CJ feedback 2026-05-08: WoW delta should be green when up, red when
  // down, regardless of any flag the API supplies. Color drives off the
  // sign first; flag is a fallback when no number is present.
  function deltaColor(p, flag) {
    if (typeof p === 'number' && !isNaN(p)) {
      if (p > 0) return '#16a34a';
      if (p < 0) return '#dc2626';
      return '#757575';
    }
    if (flag === 'green') return '#16a34a';
    if (flag === 'red') return '#dc2626';
    if (flag === 'yellow' || flag === 'orange') return '#d97706';
    return '#757575';
  }

  if (loading) return <div className="card text-sm text-gray-500">Loading owner dashboard…</div>;

  return (
    <div className="space-y-4">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 11, color: '#9e9e9e' }}>
          {refreshedAt ? `Refreshed ${refreshedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} · auto every 60s` : ''}
          {cogsAsOf ? ` · COGS as of ${cogsAsOf} ET${cogsStale ? ' (last good)' : ''}` : ''}
        </div>
        <button onClick={() => load()}
          style={{ fontSize: 12, padding: '6px 12px', border: '1px solid #d0d4d9',
                    background: '#fff', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
          ↻ Refresh
        </button>
      </div>
      {/* Aggregate top-line */}
      <div className="card" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
        <div>
          <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Revenue today</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#212121' }}>{fmtMoney(totals.revenue)}</div>
          <div style={{ fontSize: 11, color: '#757575' }}>across 4 cafés · Square net, after discounts</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Transactions today</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#212121' }}>{totals.tx.toLocaleString()}</div>
          <div style={{ fontSize: 11, color: '#757575' }}>across 4 cafés</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Avg ticket</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#212121' }}>{totals.tx > 0 ? '$' + (totals.revenue / totals.tx).toFixed(2) : '—'}</div>
          <div style={{ fontSize: 11, color: '#757575' }}>company-wide</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Labor today</div>
          <div style={{ fontSize: 28, fontWeight: 700,
                         color: totals.laborPct == null ? '#bdbdbd'
                              : totals.laborPct < 25 ? '#16a34a'
                              : totals.laborPct < 35 ? '#d97706'
                              : '#dc2626' }}>
            {totals.laborPct != null ? `${totals.laborPct.toFixed(1)}%` : '—'}
          </div>
          <div style={{ fontSize: 11, color: '#757575' }}>
            {totals.laborCost > 0
              ? `$${totals.laborCost.toFixed(0)} cost · ${totals.laborHours.toFixed(1)} hrs · $${totals.revenue.toFixed(0)} sales`
              : 'No labor data yet'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Labor right now</div>
          <div style={{ fontSize: 28, fontWeight: 700,
                         color: totals.hourPct == null ? '#bdbdbd'
                              : totals.hourPct < 25 ? '#16a34a'
                              : totals.hourPct < 35 ? '#d97706'
                              : '#dc2626' }}>
            {totals.hourPct != null ? `${totals.hourPct.toFixed(1)}%` : '—'}
          </div>
          <div style={{ fontSize: 11, color: '#757575' }}>
            {totals.burnRate > 0
              ? `$${totals.burnRate.toFixed(0)}/hr burn · ${cards.reduce((n,c)=>n+(c.onClock||0),0)} on clock`
              : 'No one clocked in'}
          </div>
        </div>
        {/* Theoretical COGS — two bases so the % ties to the $ (CJ 2026-07-18):
            headline rate is over COSTED sales; below it the same real cogs$ as a
            % of ALL sales. Converge as recipes get built. */}
        <div>
          <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Theoretical COGS</div>
          <div style={{ fontSize: 28, fontWeight: 700,
                         color: totals.foodCostPct == null ? '#bdbdbd'
                              : totals.foodCostPct < 30 ? '#16a34a'
                              : totals.foodCostPct < 35 ? '#d97706'
                              : '#dc2626' }}>
            {totals.foodCostPct != null ? `${totals.foodCostPct.toFixed(1)}%` : '—'}
            {totals.foodCostPct != null && (
              <span style={{ fontSize: 12, fontWeight: 500, color: '#9e9e9e' }}> of costed sales</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#757575' }}>
            {totals.cogs > 0
              ? `${fmtUsd(totals.cogs)} cost · ${totals.blendedCogsPct != null ? `${totals.blendedCogsPct.toFixed(1)}% of all sales` : '—'}`
              : 'No COGS yet'}
          </div>
          {totals.cogs > 0 && (
            <div style={{ fontSize: 11, color: '#9e9e9e' }}>
              {`${fmtUsd(totals.costedRev)} costed of ${fmtUsd(totals.revenue)} · ${totals.revenue > 0 ? Math.round((totals.costedRev / totals.revenue) * 100) : 0}% costed`}
            </div>
          )}
        </div>
        {/* Margin after prime cost = net sales − accumulated labor − theoretical
            cogs. NOT net profit: excludes rent/utilities/supplies/fees/taxes —
            "prime cost" (labor + COGS) is all that's deducted (CJ 2026-07-20). */}
        <div>
          <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Margin after prime cost</div>
          <div style={{ fontSize: 28, fontWeight: 700,
                         color: totals.netMargin == null ? '#bdbdbd' : totals.netMargin >= 0 ? '#16a34a' : '#dc2626' }}>
            {totals.netMargin != null ? fmtUsd(totals.netMargin) : '—'}
          </div>
          <div style={{ fontSize: 11, color: '#757575' }}>
            {totals.netMargin != null
              ? `${fmtUsd(totals.revenue)} − ${fmtUsd(totals.laborCost)} labor − ${fmtUsd(totals.cogs)} cogs${totals.netMarginPct != null ? ` · ${totals.netMarginPct.toFixed(0)}% of sales` : ''}`
              : 'Needs COGS + labor'}
          </div>
        </div>
      </div>
      {/* Per-location grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
        {cards.map(c => (
          <div key={c.key} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#212121' }}>{c.name}</div>
              {!c.error && c.onClock != null && (
                <div style={{ fontSize: 11, color: '#757575' }}>{c.onClock} on / {c.scheduled} scheduled</div>
              )}
            </div>
            {c.error ? (
              <div style={{ fontSize: 12, color: '#991b1b' }}>Error: {c.error}</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Revenue</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#212121' }}>{fmtMoney(c.revenue)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: deltaColor(c.revenueDelta, c.revenueFlag) }}>
                      {fmtPct(c.revenueDelta)} <span style={{ fontWeight: 400, color: '#9e9e9e' }}>WoW</span>
                    </div>
                    {c.revenueYoY != null && (
                      <div style={{ fontSize: 11, fontWeight: 600, color: deltaColor(c.revenueYoY) }}>
                        {fmtPct(c.revenueYoY)} <span style={{ fontWeight: 400, color: '#9e9e9e' }}>YoY</span>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Transactions</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#212121' }}>{c.tx}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: deltaColor(c.txDelta) }}>
                      {fmtPct(c.txDelta)} <span style={{ fontWeight: 400, color: '#9e9e9e' }}>WoW</span>
                    </div>
                    {c.txYoY != null && (
                      <div style={{ fontSize: 11, fontWeight: 600, color: deltaColor(c.txYoY) }}>
                        {fmtPct(c.txYoY)} <span style={{ fontWeight: 400, color: '#9e9e9e' }}>YoY</span>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div style={{ fontSize: 11, color: '#757575' }}>
                    Avg ticket {c.avgTicket > 0 ? '$' + c.avgTicket.toFixed(2) : '—'}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {c.avgTicketDelta != null && (
                      <div style={{ fontSize: 11, fontWeight: 600, color: deltaColor(c.avgTicketDelta) }}>
                        {fmtPct(c.avgTicketDelta)} <span style={{ fontWeight: 400, color: '#9e9e9e' }}>WoW</span>
                      </div>
                    )}
                    {c.avgTicketYoY != null && (
                      <div style={{ fontSize: 10, fontWeight: 600, color: deltaColor(c.avgTicketYoY) }}>
                        {fmtPct(c.avgTicketYoY)} <span style={{ fontWeight: 400, color: '#9e9e9e' }}>YoY</span>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  {/* Cumulative labor for today (cost / sales × 100) */}
                  <div style={{ padding: '6px 8px', background: '#fafafa', borderRadius: 6,
                                borderLeft: `3px solid ${c.laborFlag === 'green' ? '#16a34a' : c.laborFlag === 'red' ? '#dc2626' : c.laborFlag === 'amber' ? '#d97706' : '#bdbdbd'}`,
                                fontSize: 11 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 9 }}>Today</span>
                      <span style={{ fontWeight: 700, color: '#212121' }}>
                        {c.laborPct != null ? `${c.laborPct.toFixed(1)}%` : '—'}
                      </span>
                    </div>
                    <div style={{ color: '#666', marginTop: 2, fontSize: 10 }}>
                      {c.laborCost ? `$${c.laborCost.toFixed(0)} / ${c.laborHours.toFixed(1)} hrs` : '—'}
                    </div>
                  </div>
                  {/* Current-hour live labor (cost-so-far-this-hour / sales-so-far-this-hour × 100) */}
                  <div style={{ padding: '6px 8px', background: '#fafafa', borderRadius: 6,
                                borderLeft: `3px solid ${c.hourFlag === 'green' ? '#16a34a' : c.hourFlag === 'red' ? '#dc2626' : c.hourFlag === 'amber' ? '#d97706' : '#bdbdbd'}`,
                                fontSize: 11 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 9 }}>Right now</span>
                      <span style={{ fontWeight: 700, color: '#212121' }}>
                        {c.hourPct != null ? `${c.hourPct.toFixed(1)}%` : '—'}
                      </span>
                    </div>
                    <div style={{ color: '#666', marginTop: 2, fontSize: 10 }}>
                      {c.burnRate ? `$${c.burnRate.toFixed(0)}/hr · ${c.onClock} on` : 'No one on'}
                    </div>
                  </div>
                </div>
                {/* Theoretical COGS + margin after prime cost — same sub-tile style as labor */}
                <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <div style={{ padding: '6px 8px', background: '#fafafa', borderRadius: 6,
                                borderLeft: `3px solid ${c.foodCostPct == null ? '#bdbdbd' : c.foodCostPct < 30 ? '#16a34a' : c.foodCostPct < 35 ? '#d97706' : '#dc2626'}`,
                                fontSize: 11 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 9 }}>COGS</span>
                      <span style={{ fontWeight: 700, color: '#212121' }}>
                        {c.foodCostPct != null ? `${c.foodCostPct.toFixed(1)}%` : '—'}
                      </span>
                    </div>
                    <div style={{ color: '#666', marginTop: 2, fontSize: 10 }}>
                      {c.cogs != null
                        ? `${fmtUsd(c.cogs)}${c.costedPct != null ? ` · ${Math.round(c.costedPct)}% costed` : ''}`
                        : (c.costedPct === 0 ? 'no recipes yet' : '—')}
                    </div>
                  </div>
                  <div style={{ padding: '6px 8px', background: '#fafafa', borderRadius: 6,
                                borderLeft: `3px solid ${c.netMargin == null ? '#bdbdbd' : c.netMargin >= 0 ? '#16a34a' : '#dc2626'}`,
                                fontSize: 11 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#9e9e9e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 9 }}>After prime cost</span>
                      <span style={{ fontWeight: 700, color: '#212121' }}>
                        {c.netMargin != null ? fmtUsd(c.netMargin) : '—'}
                      </span>
                    </div>
                    <div style={{ color: '#666', marginTop: 2, fontSize: 10 }}>
                      {c.netMargin != null ? 'sales − labor − cogs' : 'needs cogs + labor'}
                    </div>
                  </div>
                </div>
                {/* Who's on the clock right now at this store */}
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 4 }}>
                    On the clock {c.onClockPeople.length > 0 ? `(${c.onClockPeople.length})` : ''}
                  </div>
                  {c.onClockPeople.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#9e9e9e' }}>No one clocked in</div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {c.onClockPeople.map((p, i) => (
                        <span key={i} style={{ fontSize: 12, color: '#212121', background: '#f4f6f5',
                                                borderRadius: 12, padding: '3px 10px', fontWeight: 500 }}>
                          {p.name}{sinceLabel(p.since) ? <span style={{ color: '#9e9e9e', fontWeight: 400 }}> · {sinceLabel(p.since)}</span> : null}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
